import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, Input, HostBinding, NgModule } from '@angular/core';
import { Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import * as i2 from '@angular/platform-browser';

const NOUN_PROJECT_OPTIONS = new InjectionToken('__SB_NOUN_PROJECT_ICON_SERVICE_OPTIONS__');
class NounProject {
    constructor(http, document, options) {
        this.http = http;
        this.document = document;
        this.icons = new Map();
        this.iconUrlApi = 'https://npicon.slidebean.com';
        if (options) {
            this.iconUrlApi = options.iconUrlApi;
        }
    }
    async getIconById(id) {
        if (this.icons.has(id)) {
            return Promise.resolve(this.icons.get(id));
        }
        const existingElement = this.document.querySelector(`#icon-${id}`);
        if (existingElement) {
            const html = existingElement.innerHTML;
            this.icons.set(id, html);
            return html;
        }
        const result = await this.http
            .get(`${this.iconUrlApi}?id=${id}`, {
            responseType: 'json',
        })
            .toPromise();
        if (!result)
            return;
        return this.getIconByUrl(result.url, id);
    }
    async getIconByUrl(url, id) {
        if (id && this.icons.has(id)) {
            return Promise.resolve(this.icons.get(id));
        }
        if (this.icons.has(url)) {
            return Promise.resolve(this.icons.get(url));
        }
        const html = await this.http.get(url, { responseType: 'text' }).toPromise();
        if (!html)
            return html;
        this.icons.set(id || url, html);
        return html;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProject, deps: [{ token: i1.HttpClient }, { token: DOCUMENT }, { token: NOUN_PROJECT_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProject, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProject, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NOUN_PROJECT_OPTIONS]
                }] }] });

const createSvgElement = (svgString) => {
    // Load the svg
    const wrapper = document.createElement('div');
    wrapper.innerHTML = svgString;
    const svg = wrapper.querySelector('svg');
    if (!svg)
        return null;
    // Set the svg style
    svg.style.display = 'block';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.maxWidth = '100%';
    svg.style.maxHeight = '100%';
    // Force the viewBox to be square
    const viewBox = svg.getAttribute('viewBox');
    if (viewBox) {
        let [x, y, w, h] = viewBox.split(' ').map(s => +s);
        if (w !== h) {
            const diff = Math.floor(Math.abs(w - h) / 2);
            if (w < h) {
                x -= diff;
                w = h;
            }
            else {
                y -= diff;
                h = w;
            }
            svg.setAttribute('viewBox', `${x} ${y} ${w} ${h}`);
        }
    }
    const defs = Array.from(wrapper.getElementsByTagName('def'));
    const uses = Array.from(wrapper.getElementsByTagName('use'));
    defs.forEach(def => {
        const use = uses.find(u => u.getAttribute('xlink:href') === '#' + def.getAttribute('id'));
        if (use)
            use.replaceWith(def.cloneNode(true));
    });
    return svg;
};
class NounProjectIconComponent {
    get style() {
        return this.domSanitizer.bypassSecurityTrustStyle(this.color ? `fill: ${this.color}` : '');
    }
    constructor(iconService, element, domSanitizer) {
        this.iconService = iconService;
        this.element = element;
        this.domSanitizer = domSanitizer;
        this.fetchSvg$ = new Subject();
        this.svgString$ = new BehaviorSubject('');
        this.color$ = new BehaviorSubject('');
        this.destroy$ = new Subject();
    }
    ngAfterViewInit() {
        this.fetchSvg$.pipe(debounceTime(10)).subscribe(this.fetchSvg.bind(this));
        // Trigger first load
        this.fetchSvg();
        combineLatest([this.svgString$, this.color$])
            .pipe(filter(([svgString]) => !!svgString), map(([svgString, color]) => color
            ? svgString.replace(/#[A-F0-9]{6}|black|white|#000|#fff/gi, color)
            : svgString), map(createSvgElement), takeUntil(this.destroy$))
            .subscribe(svg => {
            if (svg)
                this.loadSvg(svg);
        });
    }
    ngOnChanges(changes) {
        if (changes['color']) {
            this.color$.next(this.color);
        }
        if (changes['iconId'] || changes['iconUrl']) {
            this.fetchSvg$.next();
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
    }
    async fetchSvg() {
        if (!this.iconId && !this.iconUrl)
            return;
        try {
            const svgString = this.iconId
                ? await this.iconService.getIconById(this.iconId)
                : await this.iconService.getIconByUrl(this.iconUrl);
            if (!svgString)
                throw new Error();
            this.svgString$.next(svgString);
        }
        catch (error) {
            console.warn(`Could not load NounProject icon`, error);
        }
    }
    loadSvg(svg) {
        if (this.element && svg) {
            const wrapper = this.element.nativeElement;
            while (wrapper.firstChild) {
                wrapper.removeChild(wrapper.firstChild);
            }
            wrapper.appendChild(svg);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProjectIconComponent, deps: [{ token: NounProject }, { token: i0.ElementRef }, { token: i2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: { iconId: "iconId", iconUrl: "iconUrl", color: "color" }, host: { properties: { "style": "this.style" } }, usesOnChanges: true, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProjectIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-noun-project-icon',
                    template: '',
                }]
        }], ctorParameters: () => [{ type: NounProject }, { type: i0.ElementRef }, { type: i2.DomSanitizer }], propDecorators: { iconId: [{
                type: Input
            }], iconUrl: [{
                type: Input
            }], color: [{
                type: Input
            }], style: [{
                type: HostBinding,
                args: ['style']
            }] } });

const DIRECTIVES = [NounProjectIconComponent];
class NounProjectIconModule {
    static forRoot(options) {
        return {
            ngModule: NounProjectIconModule,
            providers: [
                {
                    provide: NOUN_PROJECT_OPTIONS,
                    useFactory() {
                        return options;
                    },
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProjectIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: NounProjectIconModule, declarations: [NounProjectIconComponent], imports: [CommonModule], exports: [NounProjectIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProjectIconModule, providers: [provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NounProjectIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES,
                    exports: DIRECTIVES,
                    imports: [CommonModule],
                    providers: [provideHttpClient(withInterceptorsFromDi())],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NOUN_PROJECT_OPTIONS, NounProject, NounProjectIconComponent, NounProjectIconModule };
